import { useEffect, useState, type FormEvent } from 'react';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useCreateClient, useUpdateClient, type SaveClientInput } from '@/hooks/useClients';
import {
  CLIENT_TYPE_LABELS, PIPELINE_LABELS, PIPELINE_STAGES,
  type Client, type ClientType, type PipelineStage,
} from '@/types/clients';

type Props = {
  open: boolean;
  onClose: () => void;
  client?: Client | null;
};

const EMPTY: SaveClientInput = {
  name: '',
  type: 'architect',
  stage: 'lead',
  website: '',
  phone: '',
  email: '',
  territory: '',
  estimatedAnnualValue: undefined,
  notes: '',
  address: { street1: '', city: '', state: '', postalCode: '', country: 'United States' },
};

export function ClientFormModal({ open, onClose, client }: Props) {
  const create = useCreateClient();
  const update = useUpdateClient();
  const [form, setForm] = useState<SaveClientInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const editing = !!client;

  useEffect(() => {
    if (open) {
      setForm(client ? hydrate(client) : EMPTY);
      setError(null);
    }
  }, [open, client]);

  function update_<K extends keyof SaveClientInput>(key: K, val: SaveClientInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function updateAddr(key: keyof NonNullable<SaveClientInput['address']>, val: string) {
    setForm((f) => ({ ...f, address: { ...f.address, [key]: val } }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing && client) {
        await update.mutateAsync({ id: client.id, ...form });
        toast.success(`Updated ${form.name}`);
      } else {
        await create.mutateAsync(form);
        toast.success(`Added ${form.name}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Client' : 'New Client'}
      title={editing ? client?.name ?? 'Client' : 'Add a client.'}
      description={editing ? 'Update account details and pipeline status.' : 'Create a new account in the CRM.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="client-form" loading={submitting}>
            {editing ? 'Save Changes' : 'Create Client'}
          </Button>
        </>
      }
    >
      <form id="client-form" onSubmit={onSubmit} className="space-y-6 py-2">
        {/* Identity */}
        <FieldGroup label="Identity">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Account name"
              required
              placeholder="Gensler — Downtown LA"
              leftIcon={<Building2 size={15} />}
              value={form.name}
              onChange={(e) => update_('name', e.target.value)}
              className="md:col-span-2"
            />
            <Select
              label="Type"
              value={form.type}
              onChange={(e) => update_('type', e.target.value as ClientType)}
              options={Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            />
            <Select
              label="Pipeline Stage"
              value={form.stage}
              onChange={(e) => update_('stage', e.target.value as PipelineStage)}
              options={PIPELINE_STAGES.map((s) => ({ value: s, label: PIPELINE_LABELS[s] }))}
            />
            <Input
              label="Territory"
              placeholder="Western US, Canada, etc."
              value={form.territory ?? ''}
              onChange={(e) => update_('territory', e.target.value)}
            />
            <Input
              label="Estimated Annual Value"
              type="number"
              placeholder="0"
              value={form.estimatedAnnualValue ?? ''}
              onChange={(e) => update_('estimatedAnnualValue', e.target.value ? Number(e.target.value) : undefined)}
              hint="In USD."
            />
          </div>
        </FieldGroup>

        {/* Contact */}
        <FieldGroup label="Contact">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Phone"   value={form.phone ?? ''}   onChange={(e) => update_('phone', e.target.value)} />
            <Input label="Email"   type="email" value={form.email ?? ''} onChange={(e) => update_('email', e.target.value)} />
            <Input label="Website" value={form.website ?? ''} onChange={(e) => update_('website', e.target.value)} className="md:col-span-2" />
          </div>
        </FieldGroup>

        {/* Address */}
        <FieldGroup label="Address">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Street 1"
              value={form.address?.street1 ?? ''}
              onChange={(e) => updateAddr('street1', e.target.value)}
              className="md:col-span-2"
            />
            <Input label="City"        value={form.address?.city ?? ''}       onChange={(e) => updateAddr('city', e.target.value)} />
            <Input label="State"       value={form.address?.state ?? ''}      onChange={(e) => updateAddr('state', e.target.value)} />
            <Input label="Postal Code" value={form.address?.postalCode ?? ''} onChange={(e) => updateAddr('postalCode', e.target.value)} />
            <Input label="Country"     value={form.address?.country ?? ''}    onChange={(e) => updateAddr('country', e.target.value)} />
          </div>
        </FieldGroup>

        {/* Notes */}
        <FieldGroup label="Notes">
          <Textarea
            placeholder="Anything worth remembering — preferences, history, key projects…"
            rows={3}
            value={form.notes ?? ''}
            onChange={(e) => update_('notes', e.target.value)}
          />
        </FieldGroup>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

function hydrate(c: Client): SaveClientInput {
  return {
    name: c.name, type: c.type, stage: c.stage,
    website: c.website ?? '', phone: c.phone ?? '', email: c.email ?? '',
    territory: c.territory ?? '', estimatedAnnualValue: c.estimatedAnnualValue,
    notes: c.notes ?? '', tags: c.tags,
    ownerUid: c.ownerUid, ownerName: c.ownerName,
    parentClientId: c.parentClientId,
    address: c.address ?? { country: 'United States' },
  };
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="kicker mb-3">{label}</div>
      {children}
    </div>
  );
}
